import { eachDayOfInterval, addHours, differenceInCalendarDays, isSameDay } from 'date-fns';
import { Option, OptionIVResult, unavailableOptionIV } from 'utils/shared/quantlib/BlackScholes';
import { ProfitPercentReason } from 'utils/shared/quantlib/returnOnCost';
import { OptionInputParams, OPTIONS_PER_CONTRACT, UnderlyingStockPosition } from './utils';
import { buildKernel, StrategyKernel } from './strategyKernel';
import {
  createOpcPricingSession,
  getOpcPricingMode,
  OpcPricingMode,
  OpcPricingSession,
} from 'utils/shared/totalfinance/opcPricing';

// 500 points renders a smooth payoff curve while keeping the chart (and Recharts
// reconciliation) light. The kernel makes even this resolution ~sub-5ms, and
// breakevens are refined by bisection so curve resolution doesn't affect them.
const DATA_POINTS_FOR_PAYOFF_GRAPH = 500;
const MAX_PROFIT_LOSS_STEPS = 0.01;
const INTERVALS_FOR_TABLE = 20;

interface StrategyBuilderConstructor {
  strategyOptionParams: OptionInputParams[];
  priceRangePercentDecimal: number;
  priceRangeMaxPercentDecimal: number;
  currentSpot: number;
  dateForPayoffGraph?: Date;
  underlyingStockPositions?: UnderlyingStockPosition[];
  pricingMode?: OpcPricingMode;
}

type StrategyLeg = ReturnType<Option['calculateProperties']> &
  Option['params'] & { calculatedPremium?: number };

interface OptionStrategyMetrics {
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
  payoff: number;
  netDebit: number;
  strategyLegs: StrategyLeg[];
}

export type PayoffTable = {
  stockPrice: number;
  stockPriceRelativeToSpot: string;
  timestamp: number;
  profitPerContract: number;
  profit: number;
  profitPercent: number | null;
  profitPercentReason: ProfitPercentReason | null;
  contractValue: number;
  // percentOfMaxRisk: number | null;
  // percentOfMaxCollateral: number | null;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
}[][];

export type PayoffGraph = {
  stockPrice: number;
  profitPerContract: number;
  profit: number;
  profitPercent: number | null;
  profitPercentReason: ProfitPercentReason | null;
  contractValue: number;
  // percentOfMaxRisk: number | null;
  // percentOfMaxCollateral: number | null;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
  isZeroCrossing?: boolean;
};

type PayoffTablePrices = {
  value: number;
  percentageChange: string;
};

function normalProbabilityDensity(x: number, mean: number, stddev: number): number {
  const exponent = Math.exp(-((x - mean) ** 2) / (2 * stddev ** 2));
  return (1 / (stddev * Math.sqrt(2 * Math.PI))) * exponent;
}

function toDollarsAndCents(value: number) {
  return parseFloat(value.toFixed(2));
}

function findBounds({
  value,
  rangePercentageDecimal,
  isExact,
}: {
  value: number;
  rangePercentageDecimal: number;
  isExact?: boolean;
}) {
  const lowerBound = value * (1 - rangePercentageDecimal);
  const upperBound = value * (1 + rangePercentageDecimal);

  if (isExact) {
    return { lowerBound, upperBound };
  } else {
    return {
      lowerBound: toDollarsAndCents(lowerBound),
      upperBound: toDollarsAndCents(upperBound),
    };
  }
}

function calculatePayoffTablePrices(
  currentPrice: number,
  rangePercentageDecimal: number,
): PayoffTablePrices[] {
  const { lowerBound, upperBound } = findBounds({ value: currentPrice, rangePercentageDecimal });

  const smallestDifference = (currentPrice - lowerBound) / 10;
  const roundingFunction = getRoundingFunction(smallestDifference);

  const belowPrices = generateUniquePrices(lowerBound, currentPrice, roundingFunction, 10);
  const abovePrices = generateUniquePrices(currentPrice, upperBound, roundingFunction, 10, false);

  const allPrices = [...belowPrices.reverse(), currentPrice, ...abovePrices];

  return allPrices.map((value) => ({
    value,
    percentageChange: getPercentageChange(currentPrice, value),
  }));
}

function generateUniquePrices(
  start: number,
  end: number,
  roundingFunction: (price: number) => number,
  count: number,
  isBelow = true,
): number[] {
  let prices: number[] = [];
  for (let i = 1; i <= count; i++) {
    let price = isBelow ? end - (end - start) * (i / 10) : start + (end - start) * (i / 10);
    price = roundingFunction(price);
    while (prices.includes(price)) {
      price = isBelow ? price - 0.01 : price + 0.01;
      price = roundingFunction(price);
    }
    prices.push(price);
  }
  return prices;
}

function getRoundingFunction(smallestDifference: number): (price: number) => number {
  if (smallestDifference > 2) {
    return (price: number) => Math.round(price);
  } else if (smallestDifference > 0.5) {
    return (price: number) => Math.round(price * 2) / 2; // Nearest 0.5
  } else if (smallestDifference > 0.1) {
    return (price: number) => Math.round(price * 10) / 10; // Nearest 0.1
  } else {
    return (price: number) => Math.round(price * 100) / 100; // Nearest 0.01 (penny)
  }
}

function getPercentageChange(original: number, changed: number): string {
  const change = ((changed - original) / original) * 100;
  return `${change.toFixed(1)}%`;
}

function createDateInterval(startDate: Date, endDate: Date, maxIntervalDays: number): Date[] {
  const totalDays = differenceInCalendarDays(endDate, startDate) + 1;

  if (isSameDay(startDate, endDate)) {
    return [startDate];
  } else if (totalDays <= maxIntervalDays) {
    return eachDayOfInterval({ start: startDate, end: endDate });
  } else {
    const intervals = [];
    const step = totalDays / maxIntervalDays;
    let currentDate = startDate;

    for (let i = 0; i < maxIntervalDays; i++) {
      if (i === maxIntervalDays - 1) {
        // Ensure the last date is always the endDate
        intervals.push(endDate);
      } else {
        if (i > 0) {
          // Skip the first iteration to include the startDate as the first interval
          currentDate = addHours(currentDate, step * 24);
        }
        intervals.push(currentDate);
      }
    }

    return intervals;
  }
}

const MINIMUM_BREAK_EVEN_TO_BE_ERRATIC = 20;
function detectErraticBehavior(breakEvenPoints: number[]): boolean {
  return breakEvenPoints.length > MINIMUM_BREAK_EVEN_TO_BE_ERRATIC;
}

const calculateNpfBoundedValue = (value: number, counterValue: number): number => {
  /**
   * @note fix, this 4 * is arbitrary
   */
  const priceRange = 4 * counterValue;
  return Math.min(value, priceRange);
};

const calculateExpectedProfit = (maxProfit: number, probabilityOfProfit: number): number => {
  return maxProfit / (1 + Math.exp(-probabilityOfProfit));
};

const calculateExpectedRisk = (maxLoss: number, probabilityOfProfit: number): number => {
  return maxLoss / (1 + Math.exp(-(1 - probabilityOfProfit)));
};

export class StrategyBuilder {
  public readonly strategyOptionParams: OptionInputParams[];
  public readonly legs: Option[];
  public readonly underlyingStockPositions: UnderlyingStockPosition[];
  public readonly strategMetrics: OptionStrategyMetrics;
  public readonly priceRangePercentDecimal: number;
  public readonly priceRangeMaxPercentDecimal: number;
  public readonly currentSpot: number;
  public readonly pricingMode: OpcPricingMode;

  constructor({
    strategyOptionParams,
    priceRangePercentDecimal,
    priceRangeMaxPercentDecimal,
    currentSpot,
    underlyingStockPositions = [],
    pricingMode = getOpcPricingMode(),
  }: StrategyBuilderConstructor) {
    this.strategyOptionParams = strategyOptionParams;
    this.legs = strategyOptionParams.map((params) => new Option(params));
    this.priceRangePercentDecimal = priceRangePercentDecimal;
    this.priceRangeMaxPercentDecimal = priceRangeMaxPercentDecimal;
    this.currentSpot = currentSpot;
    this.pricingMode = pricingMode;
    this.underlyingStockPositions = underlyingStockPositions;
    this.strategMetrics = this.calculateStrategyMetrics(this.legs, this.underlyingStockPositions);
  }

  // Pure pricing kernel, built once and reused for the graph, table, and any
  // time-scrubbing. Replaces the per-point `new Option(...)` + IV re-solve loop.
  private _kernel: StrategyKernel | null = null;
  public get kernel(): StrategyKernel {
    if (!this._kernel) {
      this._kernel = buildKernel(this.strategyOptionParams, this.underlyingStockPositions);
    }
    return this._kernel;
  }

  private _pricing: OpcPricingSession | null = null;
  public get pricing(): OpcPricingSession {
    if (!this._pricing)
      this._pricing = createOpcPricingSession({ kernel: this.kernel, mode: this.pricingMode });
    return this._pricing;
  }

  public getFullStrategy(maxExpirationDate: Date) {
    const { lowerBound, upperBound } = findBounds({
      value: this.currentSpot,
      rangePercentageDecimal: this.priceRangeMaxPercentDecimal,
    });
    const now = new Date();
    const daysForPayoffTable = createDateInterval(now, maxExpirationDate, INTERVALS_FOR_TABLE);
    const maxProfitLoss = this.calculateStrategyMaxProfitLoss({
      legs: this.legs,
      underlyingStockPositions: this.underlyingStockPositions,
      startPrice: lowerBound,
      endPrice: upperBound,
      dateAtCalculation: maxExpirationDate,
    });

    /**
     * @todo Divide dates into 1400 increments and go across the entire range of prices
     * Then pluck out the ones you need for graph and table
     * Cache the results within a single ticker
     */

    const graphDataPoints = this.calculateGraphDataPoints({
      startPrice: lowerBound,
      endPrice: upperBound,
      steps: DATA_POINTS_FOR_PAYOFF_GRAPH,
      dateAtCalculation: maxExpirationDate,
      maxProfitLoss,
    });

    const breakEvenPoints: number[] = graphDataPoints.breakEvens;

    /**
     * @todo add step for percent of max loss and collateral or just inside the UI?
     */

    const probabilityOfProfit = this.calculateProbabilityOfProfit(
      this.legs,
      this.underlyingStockPositions,
      breakEvenPoints,
      maxExpirationDate,
    );
    const payoffTable = this.calculatePayoffTable({
      spot: this.currentSpot,
      priceRangePercentDecimal: this.priceRangePercentDecimal,
      days: daysForPayoffTable,
      maxProfitLoss,
    });

    const hasErraticBehavior = detectErraticBehavior(breakEvenPoints);

    return {
      legs: this.legs,
      strategyMetrics: this.strategMetrics,
      breakEvenPoints: breakEvenPoints,
      probabilityOfProfit: probabilityOfProfit,
      maxProfitLoss: maxProfitLoss,
      payoffTable: payoffTable,
      payoffGraph: graphDataPoints.payoff,
      greeksAcrossPrices: graphDataPoints.greeks,
      hasErraticBehavior,
      // The exact bounds + step count used for `payoffGraph`. Time-scrubbing
      // must sweep on this same grid so the live curve and the dotted "at
      // expiration" reference stay index-aligned in the chart (they're zipped
      // by index in PayoffChart).
      graphGrid: { lowerBound, upperBound, steps: DATA_POINTS_FOR_PAYOFF_GRAPH },
    };
  }

  /**
   * @todo move this to the Black Scholes file?
   */
  private calculateGreekForLeg({
    greekValue,
    numberOfContracts = 1,
  }: {
    greekValue: number;
    isLong: boolean;
    numberOfContracts?: number;
  }) {
    return greekValue * numberOfContracts * OPTIONS_PER_CONTRACT;
  }

  /**
   * @todo should name change and just be greeks? What else should be in here? Ex. I already moved probability of profit to a separate calculation
   */
  public calculateStrategyMetrics(
    legs: Option[],
    underlyingStockPositions: UnderlyingStockPosition[],
    isLogging = false,
  ): OptionStrategyMetrics {
    const strategyLegs: StrategyLeg[] = [];
    let totalPayoff = 0;
    let totalDelta = 0;
    let totalGamma = 0;
    let totalTheta = 0;
    let totalVega = 0;
    let totalRho = 0;
    let totalNetDebit = 0;

    legs.forEach((leg, i) => {
      const updatedLeg = new Option({
        ...leg.params,
        premium: leg.params.isUsingCalculatedPremium ? undefined : leg.params.premium,
      });

      const properties = updatedLeg.calculateProperties();

      const legDelta = isNaN(properties.delta)
        ? 0
        : this.calculateGreekForLeg({
            greekValue: properties.delta,
            isLong: leg.params.isLong,
            numberOfContracts: leg.params.numberOfContracts,
          });

      const legGamma = isNaN(properties.gamma)
        ? 0
        : this.calculateGreekForLeg({
            greekValue: properties.gamma,
            isLong: leg.params.isLong,
            numberOfContracts: leg.params.numberOfContracts,
          });

      const legTheta = isNaN(properties.theta)
        ? 0
        : this.calculateGreekForLeg({
            greekValue: properties.theta,
            isLong: leg.params.isLong,
            numberOfContracts: leg.params.numberOfContracts,
          });

      const legVega = isNaN(properties.vega)
        ? 0
        : this.calculateGreekForLeg({
            greekValue: properties.vega,
            isLong: leg.params.isLong,
            numberOfContracts: leg.params.numberOfContracts,
          });

      const legRho = isNaN(properties.rho)
        ? 0
        : this.calculateGreekForLeg({
            greekValue: properties.rho,
            isLong: leg.params.isLong,
            numberOfContracts: leg.params.numberOfContracts,
          });

      totalDelta = totalDelta + legDelta;
      totalGamma = totalGamma + legGamma;
      totalTheta = totalTheta + legTheta;
      totalVega = totalVega + legVega;
      totalRho = totalRho + legRho;

      totalNetDebit = totalNetDebit + properties.netDebit;
      totalPayoff = totalPayoff + properties.payoffAtSpot;

      strategyLegs.push({
        ...updatedLeg.params,
        delta: legDelta,
        gamma: legGamma,
        theta: legTheta,
        vega: legVega,
        rho: legRho,
        value: properties.value,
        impliedVolatility: properties.impliedVolatility,
        impliedVolatilityResult: properties.impliedVolatilityResult,
        netDebit: properties.netDebit,
        payoffAtSpot: properties.payoffAtSpot,
        calculatedPremium: properties.calculatedPremium,
        isUsingCalculatedPremium: properties.isUsingCalculatedPremium,
      });

      if (isLogging) {
        console.log(
          'leg i',
          i,
          {
            spot: updatedLeg.params.spot,
            totalPayoff: totalPayoff,
            calculatedPremium: properties.calculatedPremium,
            payoffAtSpot: properties.payoffAtSpot,
          },
          updatedLeg.params,
        );
      }
    });

    const currentStockPrice = legs[0]?.params?.spot || 0;
    const { totalProfit: totalStockProfit, totalDelta: totalStockDelta } =
      Option.calculateUnderlyingStockPositionMetrics({
        underlyingStockPositions,
        currentPrice: currentStockPrice,
      });
    totalPayoff = totalPayoff + totalStockProfit;
    totalDelta = totalDelta + totalStockDelta;

    const strategy: OptionStrategyMetrics = {
      delta: totalDelta,
      gamma: totalGamma,
      theta: totalTheta,
      vega: totalVega,
      rho: totalRho,
      payoff: totalPayoff,
      netDebit: totalNetDebit,
      strategyLegs,
    };

    return strategy;
  }

  public calculateProbabilityOfProfit(
    legs: Option[],
    underlyingStockPositions: UnderlyingStockPosition[],
    breakEvenPoints: number[],
    dateAtCalculation: Date,
  ): number {
    return Option.calculateStrategyProbabilityOfProfit(
      legs,
      underlyingStockPositions,
      breakEvenPoints,
      dateAtCalculation,
    );
  }

  public calculateStrategyMaxProfitLoss({
    legs,
    underlyingStockPositions,
    startPrice,
    endPrice,
    dateAtCalculation,
  }: {
    legs: Option[];
    underlyingStockPositions: UnderlyingStockPosition[];
    startPrice: number;
    endPrice: number;
    dateAtCalculation: Date;
  }) {
    return Option.calculateStrategyMaxProfitLoss({
      strategy: legs,
      underlyingStockPositions: underlyingStockPositions,
      lowestSpot: startPrice,
      highestSpot: endPrice,
      stepUnit: MAX_PROFIT_LOSS_STEPS,
      dateAtCalculation,
    });
  }

  public calculatePayoffTable({
    spot,
    priceRangePercentDecimal,
    days,
  }: {
    spot: number;
    priceRangePercentDecimal: number;
    days: Date[];
    maxProfitLoss: {
      maxProfit: number;
      maxLoss: number;
    };
  }): PayoffTable {
    const prices = calculatePayoffTablePrices(spot, priceRangePercentDecimal);
    const points = this.pricing.table({
      prices: prices.map((price) => price.value),
      times: days.map((day) => day.getTime()),
    });
    const table: PayoffTable = new Array(prices.length);

    for (let i = 0; i <= prices.length - 1; i++) {
      const stockPrice = prices[i];
      table[i] = new Array(days.length);
      for (let j = 0; j <= days.length - 1; j++) {
        const day = days[j];
        const point = points[i][j];
        table[i][j] = {
          stockPrice: stockPrice?.value,
          stockPriceRelativeToSpot: stockPrice?.percentageChange,
          timestamp: day.getTime(),
          profitPerContract: point.profitPerContract,
          profit: point.profit,
          profitPercent: point.profitPercent,
          profitPercentReason: point.profitPercentReason,
          contractValue: point.contractValue,
          delta: point.delta,
          gamma: point.gamma,
          theta: point.theta,
          vega: point.vega,
          rho: point.rho,
        };
      }
    }

    return table.reverse();
  }

  public calculateGraphDataPoints({
    startPrice,
    endPrice,
    steps = DATA_POINTS_FOR_PAYOFF_GRAPH,
    dateAtCalculation,
  }: {
    startPrice: number;
    endPrice: number;
    steps: number;
    dateAtCalculation: Date;
    maxProfitLoss: { maxProfit: number; maxLoss: number };
  }) {
    const { payoff, breakEvens } = this.pricing.curve({
      from: startPrice,
      to: endPrice,
      steps,
      asOf: dateAtCalculation.getTime(),
      injectStrikes: true, // sample exactly at each strike so expiration corners stay sharp
    });

    // greeksAcrossPrices is just the greek columns of each payoff point.
    const greeks = payoff.map((point) => ({
      stockPrice: point.stockPrice,
      delta: point.delta,
      gamma: point.gamma,
      theta: point.theta,
      vega: point.vega,
      rho: point.rho,
    }));

    return { payoff, greeks, breakEvens };
  }

  public calculateImpliedVolatilities(): OptionIVResult[] {
    return this.legs.map((leg) => leg.calculateImpliedVolatilityDetailed());
  }

  static initializedImpliedVolatilities(strategyOptionParams: OptionInputParams[]): {
    impliedVolatilities: OptionIVResult[];
    averageImpliedVolatility: number | null;
  } {
    const impliedVolatilities = strategyOptionParams.map((params) => {
      try {
        return new Option(params).calculateImpliedVolatilityDetailed();
      } catch {
        return unavailableOptionIV('invalid-input');
      }
    });
    // Never present a partial average (or an estimate) as the solved strategy IV.
    const solved = impliedVolatilities.filter(
      (result): result is Extract<OptionIVResult, { status: 'solved' }> =>
        result.status === 'solved',
    );
    const averageImpliedVolatility =
      solved.length > 0 && solved.length === impliedVolatilities.length
        ? solved.reduce((sum, result) => sum + result.impliedVolatility, 0) / solved.length
        : null;

    return {
      impliedVolatilities,
      averageImpliedVolatility,
    };
  }

  static generateProbabilityData(optionData: {
    currentPrice: number;
    strikePrice: number;
    timeToExpiration: number;
    riskFreeRate: number;
    volatility: number;
    spot: number;
    data: PayoffGraph[];
    priceRangePercentDecimal: number;
  }): { stockPrice: number; probability: number }[] {
    const { currentPrice, volatility, timeToExpiration, data } = optionData;

    const stddev = currentPrice * volatility * Math.sqrt(timeToExpiration);
    const mean = currentPrice;

    return data?.map(({ stockPrice }) => ({
      stockPrice: parseFloat(stockPrice.toFixed(2)),
      probability: parseFloat(
        (normalProbabilityDensity(stockPrice, mean, stddev) * 100).toFixed(2),
      ),
    }));
  }

  static calculateNormalizedProfitFactor({
    maxProfit,
    maxLoss,
    probabilityOfProfit,
  }: {
    maxProfit: number;
    maxLoss: number;
    spot: number;
    volatility: number;
    probabilityOfProfit: number;
  }): number {
    const boundedMaxProfit = calculateNpfBoundedValue(maxProfit, maxLoss);
    const boundedMaxLoss = calculateNpfBoundedValue(maxLoss, maxProfit);

    const expectedProfit = calculateExpectedProfit(boundedMaxProfit, probabilityOfProfit);
    const expectedRisk = calculateExpectedRisk(boundedMaxLoss, probabilityOfProfit);

    /**
     * @note fix, this -3 is arbitrary
     */
    return expectedProfit / expectedRisk - 3;
  }
}
